import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, ScrollToTop, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const ServicePage = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Icons for benefits based on service
  const benefitIcons = ["🎯", "💪", "⚡", "🏆", "❤️"];

  if (!service) {
    return (
      <main className="pt-32 min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">{t.servicesPage.notFound || "Service non trouvé"}</h1>
          <Button onClick={() => navigate("/services")} className="bg-primary hover:bg-primary/90">
            {t.servicesPage.backToServices || "Retour aux services"}
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32" data-testid="service-detail-page">
      <SEOHead
        title={`${service.title} | Body-Up Personal Training Nyon`}
        description={service.fullDescription?.intro?.substring(0, 160) || service.description}
        path={`/services/${service.id}`}
        image={service.image}
      />
      <section className="relative py-16 bg-gradient-to-b from-black to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={service.image} 
            alt={service.name} 
            className="w-full h-full object-cover" 
            style={{ objectPosition: service.imagePosition }}
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 to-gray-900" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <BackNav label={t.servicesPage.backToServices || "Retour aux services"} />
            
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-4xl">{service.emoji}</span>
                  <AnimatedBadge>{service.fullDescription.badge}</AnimatedBadge>
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">{service.title}</h1>
                <p className="text-lg text-gray-300 mb-6">{service.fullDescription.intro}</p>
                
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all hover:scale-105">
                    <Calendar className="w-5 h-5 mr-2" />
                    {t.servicesPage.bookCta}
                  </Button>
                </a>
              </div>
              
              {/* Smaller image */}
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-pink-500/20 rounded-3xl blur-xl opacity-50" />
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="relative rounded-2xl w-full shadow-2xl border border-white/10"
                  style={{ objectPosition: service.imagePosition }}
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits - WAHOU Effect */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-white mb-10 text-center">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">✨ {t.servicesPage.benefits}</span>
            </h2>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {service.fullDescription.benefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <div className="group relative p-6 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent rounded-2xl border border-primary/30 hover:border-primary transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/20 overflow-hidden">
                  {/* Animated glow */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-primary/30 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative z-10 flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <span className="text-2xl">{benefitIcons[index % benefitIcons.length]}</span>
                    </div>
                    <span className="text-white font-medium pt-2 group-hover:text-primary transition-colors">{benefit}</span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* For Who */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="relative bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl p-10 border border-primary/30 overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-white mb-4">👤 {t.servicesPage.whoIsItFor}</h2>
                <p className="text-gray-300 text-lg">{service.fullDescription.forWho}</p>
              </div>
            </div>
          </AnimatedSection>

          {/* === NEW: Comment ça se passe + Tarif indicatif === */}
          <AnimatedSection delay={100}>
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              {/* Déroulé d'une séance */}
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black p-7" data-testid="service-flow-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t === undefined ? "Déroulé d'une séance" : "Déroulé d'une séance"}
                  </h3>
                </div>
                <ol className="space-y-3 text-sm text-zinc-300">
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary font-bold text-xs flex items-center justify-center">1</span>
                    <span>Accueil personnalisé et brief des objectifs du jour</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary font-bold text-xs flex items-center justify-center">2</span>
                    <span>Échauffement progressif adapté à ton niveau</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary font-bold text-xs flex items-center justify-center">3</span>
                    <span>Séance technique guidée 1:1 avec coach diplômé</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary font-bold text-xs flex items-center justify-center">4</span>
                    <span>Récupération + feedback et plan pour la prochaine séance</span>
                  </li>
                </ol>
              </div>

              {/* Prix indicatif → /tarifs */}
              <div className="rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 via-zinc-900 to-black p-7 flex flex-col" data-testid="service-price-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                    <Award className="w-5 h-5 text-black" />
                  </div>
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Tarif indicatif
                  </h3>
                </div>
                <p className="text-sm text-zinc-300 mb-4 leading-relaxed">
                  Tarifs dégressifs en fonction du nombre de séances. Dès <span className="font-bold text-white">65 CHF/séance</span> en pack. Consultation préalable <span className="font-bold text-primary">100 % gratuite</span> et sans engagement.
                </p>
                <ul className="space-y-1.5 text-xs text-zinc-400 mb-5">
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary" /> Packs 10 / 20 / 40 séances</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary" /> Durées 30' / 45' / 60'</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary" /> Paiement échelonné possible</li>
                </ul>
                <Link to="/tarifs" className="mt-auto inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/5 border border-white/15 text-white text-sm font-semibold hover:bg-white/10 transition-colors" data-testid="service-see-pricing">
                  Voir tous les tarifs
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </AnimatedSection>

          {/* Group Badge - EMS en groupe */}
          {service.fullDescription.groupBadge && (
            <AnimatedSection delay={150}>
              <a
                href={service.fullDescription.groupBadge.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-8 group"
                data-testid="group-ems-badge"
              >
                <div className="relative bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-primary/10 rounded-2xl p-6 border border-amber-500/30 hover:border-amber-400/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-500/10 overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/15 rounded-full blur-3xl group-hover:bg-amber-500/25 transition-colors" />
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Users className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors">
                            {service.fullDescription.groupBadge.text}
                          </span>
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-bold rounded-full border border-amber-500/30 animate-pulse">
                            WAHOU
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">{service.fullDescription.groupBadge.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-amber-400 group-hover:translate-x-1 transition-transform">
                      <span className="text-sm font-medium hidden sm:block">Decouvrir</span>
                      <ExternalLink className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </a>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              🚀 {t.cta.title}
            </h2>
            <p className="text-white/90 text-lg mb-8">
              {t.cta.description}
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 hover:scale-105 transition-all">
                <Calendar className="w-5 h-5 mr-2" />
                {t.cta.button}
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// FAQ Page

export default ServicePage;
